import { forwardRef, useEffect, useRef, type TextareaHTMLAttributes } from 'react';
import { cn } from '../../lib/utils';

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  /** Auto-resize up to this pixel height, then scroll. */
  maxHeight?: number;
}

/**
 * Auto-growing textarea. Mirrors the behavior of frontend-prod's ChatInput —
 * grows as the user types up to a cap, then scrolls internally.
 */
export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, maxHeight = 200, onInput, ...props }, forwardedRef) => {
    const internalRef = useRef<HTMLTextAreaElement | null>(null);

    useEffect(() => {
      resize(internalRef.current, maxHeight);
    }, [props.value, maxHeight]);

    return (
      <textarea
        ref={(node) => {
          internalRef.current = node;
          if (typeof forwardedRef === 'function') forwardedRef(node);
          else if (forwardedRef) forwardedRef.current = node;
        }}
        onInput={(e) => {
          resize(e.currentTarget, maxHeight);
          onInput?.(e);
        }}
        className={cn(
          'w-full resize-none bg-transparent text-sm text-foreground placeholder:text-muted-foreground focus:outline-none',
          'py-2 px-3',
          className,
        )}
        rows={1}
        {...props}
      />
    );
  },
);
Textarea.displayName = 'Textarea';

function resize(el: HTMLTextAreaElement | null, maxHeight: number) {
  if (!el) return;
  el.style.height = 'auto';
  el.style.height = `${Math.min(el.scrollHeight, maxHeight)}px`;
  el.style.overflowY = el.scrollHeight > maxHeight ? 'auto' : 'hidden';
}
